<template>
  <div
    class="bg-gray-100 py-6"
    style="height:calc(100vh - 70px);width: 100%;min-width: 700px"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ upsell.name }}</h1>
            <p class="mt-2 text-sm text-gray-600">
              {{ upsell.description }}
            </p>
          </div>
          <div class="flex space-x-3">
            <button
              @click="editUpsell"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg
                class="-ml-1 mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                ></path>
              </svg>
              Edit
            </button>
            <button
              @click="goBack"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg
                class="-ml-1 mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                ></path>
              </svg>
              Back to Upsells
            </button>
          </div>
        </div>
      </div>

      <!-- Upsell Stats -->
      <div class="mb-8">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-5">
          Upsell Performance
        </h3>
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div class="bg-white overflow-hidden shadow rounded-lg">
            <div class="px-4 py-5 sm:p-6">
              <dl>
                <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                  Total Views
                </dt>
                <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                  {{ upsell.views.toLocaleString() }}
                </dd>
              </dl>
            </div>
          </div>

          <div class="bg-white overflow-hidden shadow rounded-lg">
            <div class="px-4 py-5 sm:p-6">
              <dl>
                <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                  Conversion Rate
                </dt>
                <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                  {{ upsell.conversionRate }}%
                </dd>
              </dl>
            </div>
          </div>

          <div class="bg-white overflow-hidden shadow rounded-lg">
            <div class="px-4 py-5 sm:p-6">
              <dl>
                <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                  Revenue Generated
                </dt>
                <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                  ${{ upsell.revenueGenerated.toFixed(2) }}
                </dd>
              </dl>
            </div>
          </div>

          <div class="bg-white overflow-hidden shadow rounded-lg">
            <div class="px-4 py-5 sm:p-6">
              <dl>
                <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                  Average Order Value
                </dt>
                <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                  ${{ upsell.averageOrderValue.toFixed(2) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Two Column Layout -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Left Column: Upsell Details -->
        <div class="lg:col-span-2">
          <div class="bg-white shadow rounded-lg">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                Upsell Details
              </h3>
              
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <dt class="text-sm font-medium text-gray-500">Type</dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {{ upsell.type }}
                    </span>
                  </dd>
                </div>
                
                <div>
                  <dt class="text-sm font-medium text-gray-500">Status</dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    <span
                      :class="[
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        upsell.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      ]"
                    >
                      {{ upsell.status }}
                    </span>
                  </dd>
                </div>
                
                <div>
                  <dt class="text-sm font-medium text-gray-500">Price</dt>
                  <dd class="mt-1 text-sm text-gray-900">${{ upsell.price.toFixed(2) }}</dd>
                </div>
                
                <div>
                  <dt class="text-sm font-medium text-gray-500">Created At</dt>
                  <dd class="mt-1 text-sm text-gray-900">{{ formatDate(upsell.createdAt) }}</dd>
                </div>
              </div>
              
              <div class="mt-6">
                <dt class="text-sm font-medium text-gray-500">Description</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ upsell.description }}</dd>
              </div>
              
              <div class="mt-6" v-if="upsell.targetAudience">
                <dt class="text-sm font-medium text-gray-500">Target Audience</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ upsell.targetAudience }}</dd>
              </div>
            </div>
          </div>

          <!-- Recent Orders -->
          <div class="bg-white shadow rounded-lg mt-6">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                Recent Orders
              </h3>
              
              <div v-if="recentOrders.length === 0" class="text-center py-8">
                <p class="text-gray-500">No orders yet</p>
              </div>
              
              <div v-else class="space-y-4">
                <div
                  v-for="order in recentOrders"
                  :key="order.id"
                  class="border rounded-lg p-4"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <p class="text-sm font-medium text-gray-900">Order #{{ order.orderNumber }}</p>
                      <p class="text-sm text-gray-500">{{ formatDate(order.createdAt) }}</p>
                    </div>
                    <div class="text-right">
                      <p class="text-sm font-medium text-gray-900">${{ order.total.toFixed(2) }}</p>
                      <p class="text-sm text-gray-500">{{ order.status }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Quick Actions & Analytics -->
        <div class="lg:col-span-1">
          <div class="bg-white shadow rounded-lg">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                Quick Actions
              </h3>
              
              <div class="space-y-3">
                <button
                  @click="toggleUpsellStatus"
                  :class="[
                    'w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white',
                    upsell.status === 'active' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  ]"
                >
                  {{ upsell.status === 'active' ? 'Deactivate' : 'Activate' }}
                </button>
                
                <button
                  @click="duplicateUpsell"
                  class="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Duplicate
                </button>
                
                <button
                  @click="exportData"
                  class="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Export Data
                </button>
              </div>
            </div>
          </div>

          <!-- Performance Chart -->
          <div class="bg-white shadow rounded-lg mt-6">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                Performance Trend
              </h3>
              
              <div class="h-48 bg-gray-50 rounded-lg flex items-center justify-center">
                <p class="text-gray-500 text-sm">Chart placeholder</p>
              </div>
              
              <div class="mt-4 text-center">
                <p class="text-sm text-gray-500">Last 30 days</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UpsellDetail",
  data() {
    return {
      upsell: {
        id: 1,
        name: "Premium Product Bundle",
        description: "Upgrade to our premium product bundle with extended features and premium support.",
        type: "Cross-sell",
        status: "active",
        price: 99.99,
        conversionRate: 15.2,
        views: 1250,
        revenueGenerated: 1875.50,
        averageOrderValue: 123.45,
        createdAt: "2024-01-15T10:30:00Z",
        targetAudience: "Existing customers looking to upgrade their current plan"
      },
      recentOrders: [
        {
          id: 1,
          orderNumber: "ORD-001",
          total: 99.99,
          status: "Completed",
          createdAt: "2024-01-20T14:30:00Z"
        },
        {
          id: 2,
          orderNumber: "ORD-002",
          total: 149.99,
          status: "Processing",
          createdAt: "2024-01-19T16:45:00Z"
        },
        {
          id: 3,
          orderNumber: "ORD-003",
          total: 99.99,
          status: "Completed",
          createdAt: "2024-01-18T11:20:00Z"
        }
      ]
    };
  },
  mounted() {
    // Load upsell data based on route parameter
    const upsellId = this.$route.params.upsellId;
    if (upsellId) {
      this.loadUpsellData(upsellId);
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: "Upsells" });
    },
    editUpsell() {
      this.$router.push({ 
        name: "CreateUpsell", 
        query: { edit: this.upsell.id } 
      });
    },
    async loadUpsellData(id) {
      // Load upsell data from API
      console.log("Loading upsell data for ID:", id);
    },
    toggleUpsellStatus() {
      this.upsell.status = this.upsell.status === 'active' ? 'inactive' : 'active';
      // Make API call to update status
      console.log("Toggling upsell status to:", this.upsell.status);
    },
    duplicateUpsell() {
      // Navigate to create form with pre-filled data
      this.$router.push({ 
        name: "CreateUpsell", 
        query: { duplicate: this.upsell.id } 
      });
    },
    exportData() {
      // Export upsell data
      console.log("Exporting upsell data");
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    }
  }
};
</script> 